import React, { useState, useContext, useEffect, useCallback } from "react";

// MUI
import styled from "@emotion/styled";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import IconButton from "@mui/material/IconButton";

//components
import AutoCompleteSelect from "components/Common/AutoCompleteSelect";
import CustomButton from "components/Common/CustomButton";
import AutoCompleteState from "components/Common/AutoCompleteState";
import AutoCompletePartySelect from "components/Common/AutoCompletePartySelect";
import AutoCompletePcSelect from "components/Common/AutoCompletePcSelect";
import AutoCompleteDistrict from "components/Common/AutoCompleteDistrict";
import CustomTextField from "components/Common/CommonTextField";
import AutoCompletePlatform from "components/Common/AutoCompletePlatform";
import BulkUploadModal from "components/Common/BulkUploadModal";
import RowErrorDialog from "components/Common/RowErrorDialog";
import SmarGroupsField from "components/Common/SmarGroupInput";

//data
import { pageCategory, pageSubCategory } from "utils/data";

//apis
import {
  getDistrictList,
  getACList,
  getPCList,
  getStateList,
} from "apis/common/data";
import { addPages, handleBulkPagesUpload } from "apis/componentsAction/pages";

//context
import { DataContext } from "contexts/DataContext";
import { AlertContext } from "contexts/AlertContext";

const ModalContainer = styled("div")(({ theme }) => ({
  background: theme.palette.primary.light,
  borderRadius: "14px",
  padding: "1.5rem 2rem",
  overflowY: "scroll",

  "&::-webkit-scrollbar": {
    width: "5px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: `${theme.palette.secondary.main}`,
  },
  "& .platform_pageType_contianer": {
    // marginBottom: "0.4rem",
  },
  "& .platform": {
    display: "flex",
    alignItems: "center",
    marginBottom: "0.6rem",
  },
  "& .radio_name": {
    fontWeight: "400",
    fontSize: "16px",
    width: "25%",
    [theme.breakpoints.down("xl")]: {
      fontSize: "14px",
    },
  },
  "& .radio_group": {
    width: "75%",
    // marginBottom: "0.6rem",
    "& .MuiFormControlLabel-root": {
      marginRight: "1.5rem",
    },
    "& .MuiTypography-root": {
      fontSize: "16px",
      [theme.breakpoints.down("xl")]: {
        fontSize: "14px",
      },
    },
    "& .MuiRadio-root": {
      padding: "0.5rem 0.4rem",
      "& svg": {
        fontSize: "1.4rem",
        [theme.breakpoints.down("xl")]: {
          fontSize: "1.2rem",
        },
      },
    },
  },
  "& .page_option_container": {
    display: "flex",
    alignItems: "center",
    marginBottom: "1.3rem",
  },
  "& .option_name": {
    width: "25%",
    fontSize: "16px",
    color: "#4F4F4F",
    [theme.breakpoints.down("xl")]: {
      fontSize: "14px",
    },
  },
  "& .option_input": {
    width: "75%",
    display: "flex",
    gap: "0.5rem",
  },
  "& .option_input_smar_group": {
    width: "65%",
    display: "flex",
    gap: "0.5rem",
  },
  "& .button_style": {
    borderRadius: "20px",
    color: "#FFFFFF",
    background: theme.palette.primary.main,
    width: "20%",
    fontSize: "16px",
    [theme.breakpoints.down("xl")]: {
      fontSize: "14px",
      // width: "15%",
    },
  },
  "& .cancel_style": {
    borderRadius: "20px",
    color: theme.palette.primary.main,
    width: "20%",
    fontSize: "16px",
    fontWeight: "500",
    border: `1px solid ${theme.palette.primary.main}`,
    [theme.breakpoints.down("xl")]: {
      fontSize: "14px",
    },
  },
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  marginRight: "8px",
  padding: "5px",
  "& .editIcon": {
    fontSize: "1.4rem",
    color: "#4F4F4F",
    background: "#EFFFFD",
    [theme.breakpoints.down("xl")]: {
      fontSize: "1.2rem",
    },
  },
  "& .delIcon": {
    fontSize: "1.4rem",
    color: "#FF5252",
    background: "#EFFFFD",
    [theme.breakpoints.down("xl")]: {
      fontSize: "1.2rem",
    },
  },
}));

const initialState = {
  id: "",
  page_id: "",
  page_name: "",
  long_token: "",
  page_category: "",
  page_sub_category: "",
  party: "",
  region_type: {
    id: "",
    name: "",
  },
  // object_id: "",
  state: "",
  district: "",
  ac: "",
  pc: "",
  smar_group_set: "",
  country: {
    id: 50,
    name: "India",
  },
  insta_id: "",
  type: "Business",
};

const FbPageOps = ({
  handleClose,
  modalData,
  action,
  modalProps,
  handleEdit,
  handleUpdate,
}) => {
  const [state, setState] = useState(initialState);
  const [districtList, setDistrictList] = useState([]);
  const [acList, setAcList] = useState([]);
  const [pcList, setPcList] = useState([]);
  const [isModal, setIsModal] = useState(false);
  const [selectedSmarList, setSelectedSmarList] = useState([]);
  const [radioPlatform, setRadioPlatform] = useState(modalProps.platform);
  const [radioPageType, setRadioPageType] = useState("External");
  const [stateListData, setStateListData] = useState([]);
  const [pageAdditionType, setPageAdditionType] = useState("single");
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [rowErrors, setRowErrors] = useState([]);
  const [rowErrorDialogOpen, setRowErrorDialogOpen] = useState(false);
  const { stateList, partyList, mappingTable, countryList, irelandStateList } =
    useContext(DataContext);
  const { showAlert } = useContext(AlertContext);

  const getCountryName = (id) => {
    const country = countryList.find((item) => item.id === id);
    return country ? country.name : "";
  };

  useEffect(() => {
    if (modalData && !isModal) {
      setIsModal(true);
      let region = [];
      if (modalData.region_type) {
        region = mappingTable.filter((val) => {
          if (val.name.toLowerCase() === modalData.region_type.toLowerCase()) {
            return val;
          }
        });
      }

      setState({
        id: modalData.uid || "",
        page_id: modalData.page_id_str || "",
        page_name: modalData.page_name || "",
        page_category: modalData.page_category || "",
        page_sub_category: modalData.page_subcategory || "",
        party: modalData.political_party || "",
        platform: modalData.platform || "",
        insta_id: modalData.insta_id || "",
        country: {
          id: modalData.country,
          name: getCountryName(modalData.country),
        } || { id: "", name: "" },
        region_type:
          region.length > 0
            ? region[0]
            : {
                id: "",
                name: "",
              },
        state:
          modalData.region_type.toLowerCase() === "state"
            ? {
                id: modalData.content_object.pk,
                name: modalData.content_object.name,
              }
            : modalData.content_object?.state,
        district:
          modalData.region_type.toLowerCase() === "district"
            ? {
                id: modalData.content_object.pk,
                name: modalData.content_object.name,
              }
            : "",
        ac:
          modalData.region_type.toLowerCase() === "ac"
            ? {
                id: modalData.content_object.pk,
                name: modalData.content_object.name,
              }
            : "",
        pc:
          modalData.region_type.toLowerCase() === "pc"
            ? {
                id: modalData.content_object.pk,
                name: modalData.content_object.name,
              }
            : "",
        smar_group_set: modalData.smargroup_set_details || "",
        type: modalData?.insta_type || "",
      });
      if (modalData.region_type.toLowerCase() === "pc") {
        getPC(modalData.content_object.state);
      } else if (modalData.region_type.toLowerCase() === "ac") {
        getAC(modalData.content_object.state);
      } else if (modalData.region_type.toLowerCase() === "district") {
        getAC(modalData.content_object.state);
      }
    } else {
      if (
        state.region_type.name.toLowerCase() === "district" &&
        state.state !== ""
      ) {
        getDistrict(state.state);
      } else if (
        state.region_type.name.toLowerCase() === "ac" &&
        state.state !== ""
      ) {
        getAC(state.state);
      } else if (
        state.region_type.name.toLowerCase() === "pc" &&
        state.state !== ""
      ) {
        getPC(state.state);
      }
    }
    getStateListData(modalData.country);
  }, [modalData, state.state, radioPlatform]);

  const getDistrict = async (state) => {
    const { result, error } = await getDistrictList(state);
    if (error) {
      alert("Couldn't fetch the data");
      return;
    }
    setDistrictList(result);
  };

  const getAC = async (state) => {
    const { result, error } = await getACList(state);
    if (error) {
      alert("Couldn't fetch the data");
      return;
    }
    setAcList(result);
  };

  const getPC = async (state) => {
    const { result, error } = await getPCList(state);
    if (error) {
      alert("Couldn't fetch the data");
      return;
    }
    setPcList(result);
  };

  const getStateListData = async (id) => {
    const { result, err } = await getStateList(id);
    if (err) {
      alert("Couldn't fetch the data");
      return;
    }
    setStateListData(result);
  };

  const handleChange = (data, name) => {
    if (name === "country") {
      if (data) {
        getStateListData(data?.id);
      } else {
        setStateListData([]);
      }
      setState((prevState) => ({ ...prevState, [name]: data, state: "" }));
    } else if (name === "state") {
      setState((prevState) => ({
        ...prevState,
        [name]: data,
        district: "",
        ac: "",
        pc: "",
      }));
    } else if (name === "region") {
      setState((prevState) => ({
        ...prevState,
        [name]: data,
        state: "",
        district: "",
        ac: "",
        pc: "",
      }));
    } else {
      setState((prevState) => ({ ...prevState, [name]: data }));
    }
  };

  const handlePageTypeRadioChange = (event) => {
    let val = event.target.value;
    setRadioPageType(val);
    // setState(initialState);
  };

  const handlePageAdditionType = (event) => {
    let val = event.target.value;
    setPageAdditionType(val);
    // Selecting Bulk should open the upload dialog immediately - no extra click.
    if (val === "bulk") setBulkModalOpen(true);
  };

  const handleBulkModalClose = () => {
    setBulkModalOpen(false);
    setPageAdditionType("single");
  };

  const handleTextChange = (e) => {
    const { name, value } = e.target;
    setState((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleChangeRegion = (value, name) => {
    if (value) {
      setState((prevState) => ({
        ...prevState,
        region_type: value,
        state: "",
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        region_type: {
          id: "",
          name: "",
        },
      }));
    }
  };

  const handleDelete = (data) => {
    const newData = state.smar_group_set.filter(
      (item) => item.group_name !== data.group_name
    );
    if (newData.length > 0) {
      setState((prevState) => ({ ...prevState, smar_group_set: newData }));
      return;
    }
    setState((prevState) => ({ ...prevState, smar_group_set: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let smarGroupData = "";
    //for removing duplicate smargroups on edit
    if (state.smar_group_set) {
      let ids = state.smar_group_set.map((item) => item.id.toString());
      smarGroupData = [...new Set([...selectedSmarList, ...ids])];
    } else {
      if (selectedSmarList) {
        smarGroupData = [...selectedSmarList];
      }
    }

    if (pageAdditionType === "single") {
      const { status, message } = await addPages(
        state.id,
        state.page_id,
        state.long_token,
        state.page_name,
        radioPageType,
        state.page_category,
        state.page_sub_category,
        state.party,
        radioPlatform,
        state.region_type,
        state.state,
        state.country,
        state.district,
        state.ac,
        state.pc,
        smarGroupData,
        action
      );
      if (status === 400) {
        showAlert(message, "error");
      } else if (status) {
        showAlert("Data Updated", "success");
      } else {
        showAlert("Something went wrong", "error");
      }
      setState(initialState);
      if (action === "edit") {
        handleUpdate();
        handleEdit();
      }
      if (action === "create") {
        modalProps.setRefreshList("true");
        handleClose();
      }
    }
  };

  // Bulk upload is its own action, independent of the form's Save/Add submit -
  // matches the SMAR group "Bulk Add" popup pattern.
  const handleBulkUpload = async (file) => {
    const { data, error } = await handleBulkPagesUpload(file);
    if (error) {
      if (error.data?.errors?.length) {
        setRowErrors(error.data.errors);
        setRowErrorDialogOpen(true);
      } else {
        showAlert(error.data?.message || error.data, "error");
      }
    } else {
      showAlert(data?.message || `${data}`);
      modalProps.setRefreshList("true");
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <ModalContainer
        style={{ padding: action === "edit" ? "1.2rem 0rem" : "1.5rem 2rem" }}
      >
        {/* Page add on all platform single | bulk selection */}
        {action !== "edit" && (
          <div className="platform">
            <span className="radio_name">Page Additon Type</span>
            <RadioGroup
              row
              aria-labelledby="demo-radio-buttons-group-label"
              defaultValue="internal"
              name="radio-buttons-group"
              className="radio_group"
              onChange={handlePageAdditionType}
              value={pageAdditionType}
            >
              <FormControlLabel
                value="single"
                control={<Radio />}
                label="Single"
              />
              <FormControlLabel value="bulk" control={<Radio />} label="Bulk" />
            </RadioGroup>
          </div>
        )}

        {/* Page name input field */}
        {(action === "edit" || pageAdditionType === "single") && (
          <div className="page_option_container">
            <span className="option_name">Page Name</span>
            <div className="option_input">
              <CustomTextField
                value={state.page_name}
                name="page_name"
                label="Enter new page name"
                variant="outlined"
                onChange={handleTextChange}
                sx={{ width: "80%" }}
                required
              />
            </div>
          </div>
        )}

        {/* page/channel id & account username*/}
        {pageAdditionType === "single" && (
          <div className="page_option_container">
            <span className="option_name">Page ID</span>
            <div className="option_input">
              <CustomTextField
                value={state.page_id}
                name="page_id"
                label="Enter Page ID"
                variant="outlined"
                onChange={handleTextChange}
                sx={{ width: "80%" }}
                required={false}
              />
            </div>
          </div>
        )}

        {/* Country selection */}
        {((action === "create" && pageAdditionType === "single") ||
          action === "edit") && (
          <div className="page_option_container">
            <span className="option_name">Select Country</span>
            <div className="option_input">
              <FormControl
                sx={{ flex: action === "edit" ? "0.8" : "0.4" }}
                size="small"
              >
                <AutoCompleteState
                  size="small"
                  variant="outlined"
                  value={state.country}
                  options={countryList}
                  autoComplete="true"
                  label="Select Country"
                  onChange={handleChange}
                  text="country"
                  required
                />
              </FormControl>
            </div>
          </div>
        )}

        {/* region selection, state, district, ac, pc, party, page category, subcategory selection */}
        {(action === "edit" || pageAdditionType === "single") && (
          <>
            <div className="page_option_container">
              <span className="option_name">Page Category</span>
              <div className="option_input">
                <FormControl
                  sx={{ flex: action === "edit" ? "0.8" : "0.4" }}
                  size="small"
                >
                  <AutoCompleteSelect
                    size="small"
                    variant="outlined"
                    text="page_category"
                    value={state.page_category}
                    options={pageCategory}
                    autoComplete="true"
                    label="Select Category"
                    onChange={handleChange}
                    required
                  />
                </FormControl>
              </div>
            </div>
            <div className="page_option_container">
              <span className="option_name">Page Sub Category</span>
              <div className="option_input">
                <FormControl
                  sx={{ flex: action === "edit" ? "0.8" : "0.4" }}
                  size="small"
                >
                  <AutoCompleteSelect
                    size="small"
                    variant="outlined"
                    text="page_sub_category"
                    value={state.page_sub_category}
                    options={pageSubCategory}
                    autoComplete="true"
                    label="Select sub-category"
                    onChange={handleChange}
                    required
                  />
                </FormControl>
              </div>
            </div>
            <div className="page_option_container">
              <span className="option_name">Political Party</span>
              <div className="option_input">
                <FormControl
                  sx={{ flex: action === "edit" ? "0.8" : "0.4" }}
                  size="small"
                >
                  <AutoCompletePartySelect
                    size="small"
                    variant="outlined"
                    value={state.party}
                    options={partyList}
                    autoComplete="true"
                    label="Select Political Party"
                    text="party"
                    onChange={handleChange}
                    required
                  />
                </FormControl>
              </div>
            </div>
            <div className="page_option_container">
              <span className="option_name">Region</span>
              <div className="option_input">
                <FormControl
                  sx={{ flex: action === "edit" ? "0.8" : "0.4" }}
                  size="small"
                >
                  <AutoCompletePlatform
                    size="small"
                    variant="outlined"
                    value={state.region_type}
                    options={mappingTable}
                    autoComplete="true"
                    label="Select Region"
                    text="region_type"
                    onChange={handleChangeRegion}
                    required
                  />
                </FormControl>
              </div>
            </div>
            {state?.region_type.name !== "" &&
            state?.region_type?.name !== "Nation" &&
            state?.region_type?.name !== "National" ? (
              <div className="page_option_container">
                <span className="option_name">Select State</span>
                <div className="option_input">
                  <FormControl
                    sx={{ flex: action === "edit" ? "0.8" : "0.4" }}
                    size="small"
                  >
                    <AutoCompleteState
                      size="small"
                      variant="outlined"
                      value={state.state}
                      options={stateListData}
                      autoComplete="true"
                      label="Select State"
                      onChange={handleChange}
                      text="state"
                      required={
                        state.region_type.name === "State" ? true : false
                      }
                    />
                  </FormControl>
                </div>
              </div>
            ) : (
              <></>
            )}
            {state.region !== "" &&
            state.region_type?.name.toLowerCase() === "district" ? (
              <div className="page_option_container">
                <span className="option_name">Select District</span>
                <div className="option_input">
                  <FormControl
                    sx={{ flex: action === "edit" ? "0.8" : "0.4" }}
                    size="small"
                  >
                    <AutoCompleteDistrict
                      size="small"
                      variant="outlined"
                      value={state.district}
                      options={districtList}
                      autoComplete="true"
                      label="Select District"
                      text="district"
                      onChange={handleChange}
                      required={
                        state.region_type.name === "District" ? true : false
                      }
                    />
                  </FormControl>
                </div>
              </div>
            ) : (
              <></>
            )}
            {state.region !== "" &&
            state.region_type?.name.toLowerCase() === "ac" ? (
              <div className="page_option_container">
                <span className="option_name">Select AC</span>
                <div className="option_input">
                  <FormControl
                    sx={{ flex: action === "edit" ? "0.8" : "0.4" }}
                    size="small"
                  >
                    <AutoCompletePcSelect
                      size="small"
                      variant="outlined"
                      value={state.ac}
                      options={acList}
                      autoComplete="true"
                      label="Select AC"
                      text="ac"
                      onChange={handleChange}
                      required={state.region_type.name === "AC" ? true : false}
                    />
                  </FormControl>
                </div>
              </div>
            ) : (
              <></>
            )}
            {state.region !== "" &&
            state.region_type.name.toLowerCase() === "pc" ? (
              <div className="page_option_container">
                <span className="option_name">Select PC</span>
                <div className="option_input">
                  <FormControl
                    sx={{ flex: action === "edit" ? "0.8" : "0.4" }}
                    size="small"
                  >
                    <AutoCompletePcSelect
                      size="small"
                      variant="outlined"
                      value={state.pc}
                      options={pcList}
                      autoComplete="true"
                      label="Select PC"
                      text="pc"
                      onChange={handleChange}
                      required={state.region_type.name === "PC" ? true : false}
                    />
                  </FormControl>
                </div>
              </div>
            ) : (
              <></>
            )}
          </>
        )}

        {action === "edit" &&
          state.smar_group_set &&
          state.smar_group_set.map((item, index) => {
            return (
              <div className="page_option_container" key={index}>
                <span className="option_name">
                  {index === 0 ? "SMAR Groups" : ""}
                </span>
                <div className="option_input_smar_group">
                  <CustomTextField
                    value={item.group_name}
                    name="page_name"
                    label="Enter new page name"
                    variant="outlined"
                    onChange={handleTextChange}
                    sx={{ width: "92%" }}
                    disabled
                  />
                </div>
                <StyledIconButton
                  disableRipple={true}
                  onClick={() => handleDelete(item)}
                >
                  <DeleteOutlineOutlinedIcon className="delIcon" />
                </StyledIconButton>
              </div>
            );
          })}

        {(action === "edit" || pageAdditionType === "single") && (
          <SmarGroupsField
            action={action}
            platform={radioPlatform}
            onChangeIds={(ids) => setSelectedSmarList(ids)}
          />
        )}

        {action === "edit" ? (
          <div className="page_option_container">
            <div className="option_name"></div>
            <div className="option_input">
              <CustomButton
                variant="contained"
                className="button_style"
                type="submit"
              >
                Save
              </CustomButton>
              <CustomButton
                variant="outlined"
                className="cancel_style"
                onClick={handleEdit}
              >
                Cancel
              </CustomButton>
            </div>
          </div>
        ) : pageAdditionType === "bulk" ? null : (
          <div className="page_option_container">
            <div className="option_name"></div>
            <div className="option_input">
              <CustomButton
                variant="contained"
                className="button_style"
                // style={{ width: "15%" }}
                type="submit"
              >
                Add Page
              </CustomButton>
            </div>
          </div>
        )}
      </ModalContainer>
      <BulkUploadModal
        open={bulkModalOpen}
        platform={radioPlatform}
        onClose={handleBulkModalClose}
        onUpload={handleBulkUpload}
        onError={(msg) => showAlert(msg, "error")}
      />
      <RowErrorDialog
        open={rowErrorDialogOpen}
        errors={rowErrors}
        onClose={() => setRowErrorDialogOpen(false)}
      />
    </form>
  );
};

export default FbPageOps;
